
import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { 
  Carousel, 
  CarouselContent, 
  CarouselItem, 
  CarouselNext, 
  CarouselPrevious 
} from "@/components/ui/carousel";

// Array of pottery images that were provided
const potteryImages = [
  "/lovable-uploads/6238269c-1d4e-47d3-9b7f-9898605261ea.png", 
  "/lovable-uploads/81f8fc30-883f-408e-bbb6-f0670bb4ee55.png", 
  "/lovable-uploads/6d4be08b-3682-4b4b-afb9-1a4a24f53b1c.png", 
  "/lovable-uploads/fc11d0b3-17c0-43d9-9e7b-70cac7cdf6b5.png", 
  "/lovable-uploads/e1b667cd-e6a9-4927-ae11-64badba95792.png", 
  "/lovable-uploads/ab3f3936-6d24-42f0-9930-09df6906158e.png", 
  "/lovable-uploads/f76dbc95-94b3-47ae-b78c-d0ff6d63643b.png", 
  "/lovable-uploads/b1b5fa43-0204-4d07-9524-b21b42bb4a96.png"
];

export const Hero: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <section className="relative bg-gradient-to-b from-clay-50 to-background py-20 md:py-32 overflow-hidden">
      <div className="absolute inset-0 z-0 opacity-30">
        <div className="absolute top-20 right-[20%] w-64 h-64 rounded-full bg-terracotta-200 blur-3xl" />
        <div className="absolute bottom-20 left-[30%] w-72 h-72 rounded-full bg-clay-200 blur-3xl" />
      </div>
      
      <div className="container max-w-7xl mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center lg:text-left"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.5 }}
              className="text-5xl md:text-6xl font-display font-bold text-foreground leading-tight mb-6"
            >
              <span className="block">{t('hero.shapeYour')}</span>
              <span className="text-primary">{t('hero.creativeJourney')}</span>
            </motion.h1>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.5 }}
              className="text-lg text-muted-foreground mb-8 max-w-lg mx-auto lg:mx-0"
            >
              {t('hero.subtitle')}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5, duration: 0.5 }}
              className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start"
            >
              <Link to="/booking">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white">
                  {t('hero.cta')}
                </Button>
              </Link>
              <Link to="/classes">
                <Button variant="outline" size="lg">
                  {t('classes.viewAll')}
                </Button>
              </Link>
            </motion.div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.6, duration: 0.5 }}
            className="relative"
          >
            <div className="relative z-10 rounded-2xl shadow-xl overflow-hidden">
              <Carousel
                opts={{
                  align: "start",
                  loop: true,
                  duration: 15,
                }}
                className="w-full"
              >
                <CarouselContent>
                  {potteryImages.map((src, index) => (
                    <CarouselItem key={index}>
                      <div className="relative h-[400px] bg-clay-50">
                        <div className="absolute inset-0 bg-gradient-to-tr from-clay-300/20 to-transparent z-10" />
                        <img
                          src={src}
                          alt={`${t('classes.title')} ${index + 1}`}
                          className="w-full h-full object-cover object-center"
                        />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
                <CarouselPrevious className="left-2 z-20" />
                <CarouselNext className="right-2 z-20" />
              </Carousel>
            </div>
            <div className="absolute -bottom-6 -right-6 w-24 h-24 bg-terracotta-100 rounded-full z-0" />
            <div className="absolute -top-6 -left-6 w-32 h-32 bg-clay-100 rounded-full z-0" />
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
